import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { SVG_COMPONENTS, SVG_DIMENSIONS, SvgComponentKey } from '@/assets/admin';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';

export type EmptyStateType =
  | 'bulletins'
  | 'events'
  | 'residents'
  | 'funding'
  | 'messages'
  | 'organization'
  | 'property'
  | 'userManagement'
  | 'resources'
  | 'corkBoardTags'
  | 'corkBoardThreads'
  | 'corkBoardReports';

const EMPTY_STATE_CONFIG: Record<EmptyStateType, {
  icon: SvgComponentKey;
  title: string;
  message: string;
  buttonIcon: SvgComponentKey;
}> = {
  bulletins: {
    icon: 'NOTIFICATION_BELL',
    title: 'Create new bulletin',
    message: 'Create new bulletin with ease to keep your audience informed and engaged',
    buttonIcon: 'ADD_BUTTON',
  },
  events: {
    icon: 'CALENDAR_EVENT',
    title: 'Create new event',
    message: 'Take the first step towards creating the upcoming event with ease',
    buttonIcon: 'ADD_BUTTON',
  },
  residents: {
    icon: 'NEW_USER',
    title: 'No residents found',
    message: 'Please verify property is properly configured',
    buttonIcon: 'DOWNLOAD',
  },
  funding: {
    icon: 'NEW_PROPERTY',
    title: 'No funding data available',
    message: 'Please ensure that your organization is configured correctly.',
    buttonIcon: 'DOWNLOAD',
  },
  messages: {
    icon: 'EMPTY_MESSAGE',
    title: 'New Message',
    message: 'Click the "New message" button to start compiling your message',
    buttonIcon: 'ADD_BUTTON',
  },
  organization: {
    icon: 'ORGANIZATION',
    title: 'Add organization',
    message: 'Add a new organization with its own unique settings.',
    buttonIcon: 'ADD_BUTTON',
  },
  property: {
    icon: 'NEW_PROPERTY',
    title: 'Add property',
    message: 'Click on the button to start adding a new property info.',
    buttonIcon: 'ADD_BUTTON',
  },
  userManagement: {
    icon: 'NEW_USER',
    title: 'No users found',
    message: 'Please verify organization is properly configured.',
    buttonIcon: 'ADD_BUTTON',
  },
  resources: {
    icon: 'FOLDER',
    title: 'Create new resource',
    message: 'Keep your residents informed with helpful resources. Click the "New resource" button to create one.',
    buttonIcon: 'ADD_BUTTON',
  },
  corkBoardTags: {
    icon: 'EMPTY_MESSAGE',
    title: 'No tags yet',
    message: 'Create your first tag to help residents categorize their posts.',
    buttonIcon: 'ADD_BUTTON',
  },
  corkBoardThreads: {
    icon: 'EMPTY_MESSAGE',
    title: 'No threads yet',
    message: 'Threads created by residents will appear here for moderation.',
    buttonIcon: 'ADD_BUTTON',
  },
  corkBoardReports: {
    icon: 'EMPTY_MESSAGE',
    title: 'No reports',
    message: 'No reports to review. The community is looking good!',
    buttonIcon: 'ADD_BUTTON',
  },
};

interface AdminEmptyStateProps {
  type: EmptyStateType;
  onActionPress?: () => void;
  showActionButton?: boolean;
}

export const AdminEmptyState: React.FC<AdminEmptyStateProps> = ({
  type,
  onActionPress,
  showActionButton = false,
}) => {
  const styles = useStyles();
  const config = EMPTY_STATE_CONFIG[type];
  const IconComponent = SVG_COMPONENTS[config.icon];
  const iconDimensions = SVG_DIMENSIONS[config.icon];

  return (
    <View style={styles.container}>
      <View style={styles.iconContainer}>
        <IconComponent {...iconDimensions} />
      </View>

      <View style={styles.textFrame}>
        <Text style={styles.titleText}>{config.title}</Text>
        <Text style={styles.subtitleText}>{config.message}</Text>
      </View>

      {showActionButton && onActionPress && (
        <View style={styles.buttonContainer}>
          {/* Button implementation can be added here when needed */}
        </View>
      )}
    </View>
  );
};

const useStyles = () =>
  useThemedStyles(({ components, colors }) => {
    const admin = components.admin;
    return StyleSheet.create({
      container: {
        width: 240,
        height: 179,
        gap: 18,
        alignItems: 'center',
        justifyContent: 'center',
      },
      iconContainer: {
        width: 68,
        height: 68,
        justifyContent: 'center',
        alignItems: 'center',
      },
      textFrame: {
        width: 240,
        height: 93,
        gap: 6,
        alignItems: 'center',
      },
      titleText: {
        width: 200,
        height: 24,
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontWeight: '600',
        fontSize: 16,
        lineHeight: 24,
        letterSpacing: 0,
        textAlign: 'center',
        color: admin.dataTable.text,
      },
      subtitleText: {
        width: 260,
        height: 63,
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontWeight: '400',
        fontSize: 14,
        lineHeight: 21,
        letterSpacing: 0,
        textAlign: 'center',
        color: colors.text.dim,
      },
      buttonContainer: {
        marginTop: 10,
      },
    });
  });
